import React, { useEffect, useState } from "react";
import styles from "./admin-users-page.module.css";
import { useUserContext } from "@/contexts/UserContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { getAdminMirrorJwt, getUsers } from "@/api/admin";
import { AdminPartialUser } from "@boxem/core-api-schema";
import Button from "@/components/forms/Button/Button";
import IconClose from "@/components/icons/IconClose";
import { useLocation } from "wouter";
import Pagination from "@/components/Pagination/Pagination";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import SearchInput from "@/components/SearchInput";

type PaginationState = {
  page: number;
  limit: number;
};

type AdminUsersPageProps = {};

const AdminUsersPage = ({}: AdminUsersPageProps) => {
  const { user } = useUserContext();
  const [location, navigate] = useLocation();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const [users, setUsers] = useState<AdminPartialUser[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchInputText, setSearchInputText] = useState<string>("");
  const [focusedUser, setFocusedUser] = useState<AdminPartialUser | null>(null);
  const [searchLoading, setSearchLoading] = useState<boolean>(true);
  const [mirrorAccountLoading, setMirrorAccountLoading] = useState<boolean>(false);
  const [paginationState, setPaginationState] = useState<PaginationState>({
    page: 1,
    limit: 10,
  });

  const handleMirrorAccount = (uuid: string) => {
    setMirrorAccountLoading(true);
    getAdminMirrorJwt(uuid)
      .then((data) => {
        const token = data.token;
        const redirectUrl = `${window.location.origin}/auth/login?mt=${token}`; //"mt" -> (Mirror Token)
        const newTab = window.open(redirectUrl, "_blank");
        newTab.focus();
      })
      .finally(() => setMirrorAccountLoading(false))
      .catch(err => handleErrorResponseGenerically(err));
  };

  const handlePageChange = async (newPage: number) => {
    setPaginationState({
      ...paginationState,
      page: newPage,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchInputText(value);
    if (!!value && paginationState.page !== 1) {
      setPaginationState({
        ...paginationState,
        page: 1,
      });
    }
  };

  const handleGetUsers = async () => {
    setSearchLoading(true);
  
    return await getUsers(((paginationState.page - 1) * paginationState.limit), paginationState.limit, searchInputText)
      .then(res => {
        setTotalCount(res.count);
        setUsers(res.users);
      })
      .catch(err => handleErrorResponseGenerically(err))
      .finally(() => setSearchLoading(false));
  };

  useEffect(() => {
    if (user) handleGetUsers();
  }, [
    paginationState.page,
    paginationState.limit,
    searchInputText,
    user
  ]);

  return (
    <div className={styles.adminUsersPageContainer}>
      <div className={styles.leftColumnContainer}>
        <div className={styles.userSearchContainer}>
          <SearchInput
            className={styles.search}
            searchTerm={searchInputText}
            onDebounceChange={handleSearchChange}
            searchLoading={searchLoading}
            placeholder="Search by name, email, uuid, or id"
          />
        </div>

        <div className={styles.usersListContainer}>
          {users.map(user => (
            <div
              key={user.id}
              onClick={() => setFocusedUser(user)}
              className={`${styles.userItemContainer}`}
            >
              <div className={styles.userItemName}>{user.name}</div>
              <div className={styles.userItemEmail}>{user.email}</div>
            </div>
          ))}

          {(!searchLoading && users.length === 0) && (
            <div className={styles.noResultsStateContainer}>
              <div className={styles.noResultsText}>No Results ☹️</div>
            </div>
          )}
        </div>

        <div className={styles.paginationControlsContainer}>
          <Pagination
            total={totalCount || 0}
            pageSize={paginationState.limit}
            onChangePageSize={() => {}}
            onChangePage={handlePageChange}
            page={paginationState.page}
            showPageSelector={false}
            showPageSizeOptions={false}
          />
        </div>
      </div>

      <div className={styles.rightColumnContainer}>
        {focusedUser && (
          <div className={styles.userBannerContainer}>
            <div className={styles.userBannerName}>{focusedUser?.name}</div>

            <Button
              type="text"
              onClick={() => setFocusedUser(null)}
              className={styles.closeButton}
            >
              <IconClose size={25} />
            </Button>
          </div>
        )}
        <div className={styles.rightColumnInnerContainer}>
          {!focusedUser && <div className={styles.zeroStateText}>Select A User</div>}

          {focusedUser && (
            <div className={styles.focusedUserContainer}>
              <div className={styles.userDetailsContainer}>
                <div className={styles.focusedUserName}>{focusedUser?.name}</div>
                <div className={styles.focusedUserEmail}>{focusedUser?.email}</div>
                <div className={styles.focusedUserEmail}>Seller Account: {!!focusedUser?.sellerId ? focusedUser?.sellerId :"Not connected"}</div>
              </div>

              <Button
                type="primary"
                onClick={() => handleMirrorAccount(focusedUser?.uuid)}
                disabled={mirrorAccountLoading}
                className={styles.mirrorButton}
              >
                {mirrorAccountLoading ? <LoadingSpinner size="2rem" /> : "Mirror Account"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;